import { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Flex, Box } from '../../ui/Layout'
import Button from '../../ui/Button'
import { Text } from '../../ui/Typography'

const Container = styled(Flex)`
  position: relative;
  flex-direction: column;
  max-width: 600px;
  width: 100%;
  border-radius: 16px;
  background-color: black;
  overflow: hidden;
  margin-top: 40px;
  margin-bottom: 40px;
  height: calc(100vh - 80px);

  @media (max-width: 700px) {
    height: 100%;
    width: 100%;
    object-fit: cover;
    position: fixed;
    top: 0;
    left: 0;
    margin: 0;
    border-radius: 0;
  }
`
const VideoPreview = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Controls = styled(Flex)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  justify-content: center;
  gap: 16px;
`

const RecordButton = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${(props) => (props.isRecording ? '#ff0000' : '#ffffff')};
  border: 4px solid #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`

const BackButton = styled(Button)`
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 10;
`

const VideoRecording = ({ onBack, onComplete }) => {
  const videoRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const [isRecording, setIsRecording] = useState(false)
  const [recordedChunks, setRecordedChunks] = useState([])
  const [stream, setStream] = useState(null)
  const [error, setError] = useState(null)
  const streamMonitorRef = useRef(null)

  useEffect(() => {
    startCamera()
    return () => {
      cleanup()
    }
  }, [])

  const cleanup = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
    }
    if (streamMonitorRef.current) {
      clearInterval(streamMonitorRef.current)
    }
  }

  const monitorStream = (videoStream) => {
    // Clear any existing monitor
    if (streamMonitorRef.current) {
      clearInterval(streamMonitorRef.current)
    }

    // Check stream health every 2 seconds
    streamMonitorRef.current = setInterval(() => {
      const videoTrack = videoStream.getVideoTracks()[0]
      if (videoTrack && videoTrack.readyState === 'ended') {
        console.log('Stream ended, attempting to restart...')
        cleanup()
        startCamera()
      }
    }, 2000)
  }

  const startCamera = async () => {
    try {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
      const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent)
      const videoConstraints = {
        facingMode: 'user',
        aspectRatio: 16 / 9,
        ...(!isIOS &&
          isChrome && {
            width: { ideal: 1080 },
            height: { ideal: 1920 }
          })
      }

      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          sampleRate: 48000,
          channelCount: 1
        }
      })

      setStream(videoStream)
      if (videoRef.current) {
        videoRef.current.srcObject = videoStream
      }

      // Start monitoring the stream
      monitorStream(videoStream)
    } catch (err) {
      console.error('Error accessing camera:', err)
      setError('Could not access camera. Please check your permissions and try again.')
    }
  }

  const handleStartRecording = () => {
    if (!stream) return

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const mimeType = isSafari ? 'video/mp4' : 'video/webm'
    const options = {
      mimeType: mimeType,
      videoBitsPerSecond: 2500000, // 2.5 Mbps
      audioBitsPerSecond: 128000 // 128 kbps
    }

    const mediaRecorder = new MediaRecorder(stream, options)
    mediaRecorderRef.current = mediaRecorder
    setRecordedChunks([])

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        const blob = new Blob([event.data], { type: mimeType })
        const videoURL = URL.createObjectURL(blob)
        if (videoRef.current) {
          videoRef.current.srcObject = null
          videoRef.current.src = videoURL
        }
        onComplete(blob)
      }
    }

    mediaRecorder.start()
    setIsRecording(true)
  }

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }

  return (
    <Container backgroundColor="black" p={0}>
      <Flex height="100%">
        <BackButton variant="secondary" label="Back" onClick={onBack} />

        <VideoPreview ref={videoRef} autoPlay playsInline muted={true} />

        {error && (
          <Text color="error" textAlign="center">
            {error}
          </Text>
        )}

        <Controls>
          <RecordButton isRecording={isRecording} onClick={isRecording ? handleStopRecording : handleStartRecording} />
        </Controls>
      </Flex>
    </Container>
  )
}

export default VideoRecording
